import { Component, Input, inject, ViewChild, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatRadioModule } from '@angular/material/radio'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { MatTabsModule } from '@angular/material/tabs'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { firstValueFrom, lastValueFrom } from 'rxjs'
import { TemplateEditorComponent } from '../template-editor.component/template-editor.component'
import { FileUploadRequest } from '../template-editor.component/template-item/template-item.component'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import {
  Call,
  CallsService,
  AdminAgendaMetadata,
  AdminAgendaAttendee,
  CallProcessingStatus,
  MinutesStyling,
  MetricType,
  AgendaType,
  TemplateSourceType,
  AgendaAttendeeType,
  MinutesVerboseness,
  MinutesAnonymization,
  CallType,
} from '../calls.service'
import { TemplatesService } from '../templates.service'
import { Organization } from '../organizations.service'
import { TemplateItem } from '../template-editor.component/types/template.types'
import { UsersService } from '../users.service'
import { ToastService } from '../toast.service'
import { MetricsService } from '../metrics.service'
import { TemplateSourcePickerComponent } from '../template-source-picker/template-source-picker.component'
import { FileValidationService } from '../file-validation.service'
import { DocumentUploadInfo, getFileExtension, getFileName } from '../utils'
import { UploadsService, getUploadErrorMessage } from '../uploads.service'
import { MAX_AGENDA_SIZE_IN_BYTES, MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES } from '../call-document-upload/call-document-upload.component'
import {
  TemplateImportDialog,
  TemplateImportDialogInputData,
  TemplateImportDialogOutputData,
} from '../template-editor.component/template-import-dialog/template-import-dialog'
import { TemplateSaveAsDialog, TemplateSaveAsDialogOutputData } from '../template-editor.component/template-save-as-dialog/template-save-as-dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MeetingDetailsCardComponent, MeetingDetailsSaveData } from '../meeting-details-card/meeting-details-card.component'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DurationPipe } from '../duration.pipe'
import { filter, map, Observable, take } from 'rxjs'
import { FilesizePipe } from '../filesize.pipe'
import { FileUploadComponent } from '../file-upload/file-upload.component'

@Component({
  selector: 'app-call-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatTooltipModule,
    TranslateModule,
    FileUploadComponent,
    TemplateSourcePickerComponent,
    MatIconButtonSizesModule,
    MatTabsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    TemplateEditorComponent,
    MinutesStylingComponent,
    MeetingDetailsCardComponent,
    StepProgressComponent,
    DurationPipe,
    FilesizePipe,
  ],
  templateUrl: './call-setup.component.html',
  styleUrl: './call-setup.component.scss',
})
export class CallSetupComponent implements OnInit {
  @Input({ required: true })
  call!: Call

  @Input()
  canEdit = false

  @Input({ required: true })
  metadata!: AdminAgendaMetadata

  @Input({ required: true })
  organization!: Organization

  private callsService = inject(CallsService)
  private templatesService = inject(TemplatesService)
  private usersService = inject(UsersService)
  private toastService = inject(ToastService)
  private metricsService = inject(MetricsService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)
  private fileValidationService = inject(FileValidationService)
  private uploadsService = inject(UploadsService)

  @ViewChild('recordingFileUpload')
  recordingFileUploadComponent: FileUploadComponent | undefined

  @ViewChild('templateEditor')
  templateEditor: TemplateEditorComponent | undefined

  @ViewChild('templateSourcePicker')
  templateSourcePicker: TemplateSourcePickerComponent | undefined

  @ViewChild(MeetingDetailsCardComponent)
  meetingDetailsCard: MeetingDetailsCardComponent | undefined

  currentUser$ = this.usersService.currentUser$

  templateItems$!: Observable<TemplateItem[]>

  maxAgendaSize = MAX_AGENDA_SIZE_IN_BYTES
  maxCustomDocumentSize = MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES
  isImportingAgenda = false

  selectedTabIndex = 0
  showAddAttendeeForm = false
  editedAttendeeId: number | null = null
  editedAttendeeFormData: Partial<AdminAgendaAttendee> = {}
  newAttendee: Partial<AdminAgendaAttendee> = { firstName: '', lastName: '', role: null, type: AgendaAttendeeType.MEMBER, honorifics: null }

  attendeeTypes = Object.values(AgendaAttendeeType)

  isDraggingRecording = false
  recordingUploadProgress = 0
  recordingRemainingTimeSeconds: number | null = null
  isImportingRecording = false
  uploadingFile: { name: string; size: number; isVideo: boolean } | null = null

  patchCallMutation = this.callsService.patchMutation()
  updateAgendaTypeMutation = this.callsService.updateAgendaTypeMutation()
  updateTemplateItemsMutation = this.templatesService.updateTemplateItems()
  loadTemplateMutation = this.templatesService.loadTemplate()
  saveAsTemplateMutation = this.templatesService.saveCallTemplateAsModel()
  importResourceMutation = this.callsService.importResource()
  addAttendeeMutation = this.callsService.addAttendeeMutation()
  updateAttendeeMutation = this.callsService.updateAttendeeMutation()
  deleteAttendeeMutation = this.callsService.deleteAttendeeMutation()
  updateMetadataMutation = this.callsService.updateMetadataMutation()
  importRecordingMutation = this.callsService.importRecording()
  completeSetupMutation = this.callsService.completeSetup()
  removeResourceMutation = this.callsService.removeResource()
  extractParticipantsMutation = this.callsService.extractParticipantsMutation()

  ngOnInit() {
    this.templateItems$ = this.templatesService.getTemplateItems(this.call.id).pipe(map((result) => (result.isSuccess ? result.data : [])))
  }

  get stepLabels(): string[] {
    if (this.isUploadedCall) {
      return [
        'MEETING_SETUP.STEPS.MEETING_DETAILS',
        'MEETING_SETUP.STEPS.TEMPLATE_SOURCE',
        'MEETING_SETUP.STEPS.REVIEW',
        'MEETING_SETUP.STEPS.UPLOAD_RECORDING',
      ]
    }
    if (this.isMultipleAgenda) {
      return ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE']
    }
    return ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE', 'MEETING_SETUP.STEPS.REVIEW']
  }

  get isUploadedCall(): boolean {
    return this.call.type === CallType.AUDIO_UPLOAD || this.call.type === CallType.VIDEO_UPLOAD
  }

  get hasRecordingUploaded(): boolean {
    return this.call.uploadedRecording !== null
  }

  get uploadedRecordingFileName(): string | null {
    return this.call.uploadedRecording?.fileName ?? null
  }

  get uploadedRecordingIsVideo(): boolean {
    return this.call.uploadedRecording?.resourceType === 'USER_UPLOADED_VIDEO'
  }

  get uploadedRecordingSize(): number | null {
    return this.call.uploadedRecording?.size ?? null
  }

  get currentStep() {
    if (this.isUploadedCall) {
      const hasTemplateSource = this.hasAgendaUploaded || this.call.templateSourceType !== null
      if (hasTemplateSource) {
        return 4
      }
      return 2
    }
    if (this.call.agendaType === 'MULTIPLE') {
      return 3
    }
    const hasTemplateSource = this.hasAgendaUploaded || this.call.templateSourceType !== null
    if (hasTemplateSource) {
      return 4
    }
    return 3
  }

  get displayedCurrentStep() {
    const hasUnsavedTemplateChanges = this.templateEditor?.hasUnsavedChanges ?? false
    if (this.isUploadedCall && (!this.call.templateApprovedByUser || hasUnsavedTemplateChanges) && this.currentStep === 4) {
      return 3
    }
    return this.currentStep
  }

  get completedSteps(): number {
    const hasUnsavedTemplateChanges = this.templateEditor?.hasUnsavedChanges ?? false
    if (this.isUploadedCall) {
      if (!this.call.templateApprovedByUser || hasUnsavedTemplateChanges) {
        return Math.min(this.currentStep - 1, 2)
      }
      if (this.hasRecordingUploaded) {
        return this.stepLabels.length
      }
      return 3
    }
    if (this.isMultipleAgenda) {
      return 3
    }
    if (!this.call.templateApprovedByUser || hasUnsavedTemplateChanges) {
      return Math.min(this.currentStep - 1, 3)
    }
    return 4
  }

  get hasTemplateItems(): boolean {
    return (this.templateEditor?.templateItems?.length ?? 0) > 0
  }

  get hasUnsavedChanges(): boolean {
    if (this.isMultipleAgenda) {
      return false
    }
    const reviewStepThreshold = this.isUploadedCall ? 3 : 4
    if (this.currentStep < reviewStepThreshold) {
      return false
    }
    if (!this.hasTemplateItems) {
      return false
    }
    return (this.templateEditor?.hasUnsavedChanges ?? false) || !this.call.templateApprovedByUser
  }

  get hasUnconfirmedTemplate(): boolean {
    if (this.isMultipleAgenda) {
      return false
    }
    const reviewStepThreshold = this.isUploadedCall ? 3 : 4
    if (this.currentStep < reviewStepThreshold) {
      return false
    }
    if (!this.hasTemplateItems) {
      return false
    }
    return !this.call.templateApprovedByUser
  }

  get hasAgendaUploaded(): boolean {
    return this.call.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
  }

  get defaultLevelOfDetail(): MinutesVerboseness {
    return this.call.minutesVerboseness ?? this.organization.minutesVerboseness ?? MinutesVerboseness.DETAILED
  }

  get defaultAnonymization(): MinutesAnonymization {
    return this.call.minutesAnonymization ?? this.organization.minutesAnonymization ?? MinutesAnonymization.NONE
  }

  get uploadedAgendaFileName(): string | null {
    const agendaResource = this.call.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA')
    return agendaResource?.fileName ?? null
  }

  get isExtractingAgenda(): boolean {
    return this.call.processingStatus === CallProcessingStatus.GENERATING_AGENDA_ITEMS
  }

  get agendaExtractionProgress(): number | null {
    if (!this.isExtractingAgenda) {
      return null
    }
    return this.call.agendaExtractionProgress
  }

  get isAgendaExtractionFailed(): boolean {
    return this.call.agendaExtractionFailed
  }

  get isMultipleAgenda(): boolean {
    return this.call.agendaType === 'MULTIPLE'
  }

  get meetingDetailsStepNumber(): number {
    return 1
  }

  get agendaTypeStepNumber(): number {
    return 2
  }

  get templateSourceStepNumber(): number {
    return this.isUploadedCall ? 2 : 3
  }

  get reviewStepNumber(): number {
    return this.isUploadedCall ? 3 : 4
  }

  get uploadRecordingStepNumber(): number {
    return 4
  }

  get isUploadAgendaForTemplateSelected(): boolean {
    return this.call.templateSourceType === TemplateSourceType.UPLOAD
  }

  get isCreateTemplateSelected(): boolean {
    return this.call.templateSourceType === TemplateSourceType.CREATE
  }

  get isLoadTemplateSelected(): boolean {
    return this.call.templateSourceType === TemplateSourceType.LOAD
  }

  get shouldConfirmTemplateReplace(): boolean {
    if (this.call.templateSourceType === null) {
      return false
    }
    if (this.isCreateTemplateSelected && (this.templateEditor?.templateItems.length ?? 0) === 0) {
      return false
    }
    return true
  }

  get attendeeNames(): string[] {
    return this.metadata.attendees.map((attendee) => `${attendee.firstName} ${attendee.lastName}`)
  }

  get anyAttendeeMissingHonorific(): boolean {
    return this.metadata.attendees.some((attendee) => attendee.honorifics === null || attendee.honorifics === undefined || attendee.honorifics.trim() === '')
  }

  formatHonorific(honorific: string | null | undefined): string {
    if (honorific === null || honorific === undefined) {
      return ''
    }
    const trimmed = honorific.trim()
    if (trimmed === '') {
      return ''
    }
    const endsWithPunctuation = /[.!?]$/.test(trimmed)
    return endsWithPunctuation ? trimmed : `${trimmed}.`
  }

  get groupedAttendees(): { type: AgendaAttendeeType; attendees: AdminAgendaAttendee[] }[] {
    const groups = new Map<AgendaAttendeeType, AdminAgendaAttendee[]>()

    const typeOrder = [
      AgendaAttendeeType.CHAIR,
      AgendaAttendeeType.MEMBER,
      AgendaAttendeeType.STANDING_ATTENDEE,
      AgendaAttendeeType.ATTENDEE_BY_INVITATION,
      AgendaAttendeeType.OBSERVER,
      AgendaAttendeeType.COMPANY_SECRETARY,
      AgendaAttendeeType.APOLOGY,
      AgendaAttendeeType.OTHER,
    ]

    this.metadata.attendees.forEach((attendee) => {
      const existing = groups.get(attendee.type) ?? []
      groups.set(attendee.type, [...existing, attendee])
    })

    return typeOrder
      .filter((type) => groups.has(type))
      .map((type) => ({
        type,
        attendees: groups.get(type)!,
      }))
  }

  async onUploadConfirmationRequested() {
    const confirmed = await this.showTemplateReplaceConfirmation()
    if (confirmed && this.templateSourcePicker !== undefined) {
      this.templateSourcePicker.openFileDialog()
    }
  }

  async onAgendaFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      return
    }
    this.isImportingAgenda = true
    try {
      await this.callsService.importResource().mutateAsync({
        callId: this.call.id,
        data: {
          fileType: 'AGENDA',
          fileExtension: documentUploadInfo.extension,
          uploadKey: documentUploadInfo.key,
          fileName: documentUploadInfo.fileName,
        },
      })
      this.isImportingAgenda = false
      await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
      this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOADED')
      this.scrollToStep(this.reviewStepNumber)
    } catch (error) {
      console.error('Failed to upload agenda:', error)
      this.isImportingAgenda = false
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOAD_FAILED')
      }
    }
  }

  onRecordingUploadCardClick() {
    if (this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.openFileDialog()
    }
  }

  onRecordingDragOver(event: DragEvent) {
    event.preventDefault()
    this.isDraggingRecording = true
  }

  onRecordingDragLeave() {
    this.isDraggingRecording = false
  }

  onRecordingDrop(event: DragEvent) {
    event.preventDefault()
    this.isDraggingRecording = false
    const files = event.dataTransfer?.files ?? null
    if (files !== null && files.length > 0 && this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.fileInputChanged({ target: { files } })
    }
  }

  cancelRecordingUpload() {
    if (this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.removeFile()
    }
    this.recordingUploadProgress = 0
    this.recordingRemainingTimeSeconds = null
    this.uploadingFile = null
  }

  onRecordingProgressChange(event: { progress: number; remainingTimeSeconds: number | null }) {
    this.recordingUploadProgress = event.progress
    this.recordingRemainingTimeSeconds = event.remainingTimeSeconds
    const chosenFile = this.recordingFileUploadComponent?.chosenFile
    if (this.uploadingFile === null && chosenFile !== null && chosenFile !== undefined) {
      this.uploadingFile = {
        name: chosenFile.name,
        size: chosenFile.size,
        isVideo: chosenFile.type.startsWith('video'),
      }
    }
  }

  async onRecordingFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      return
    }
    this.recordingUploadProgress = 0
    this.recordingRemainingTimeSeconds = null
    this.isImportingRecording = true
    try {
      await this.importRecordingMutation.mutateAsync({
        callId: this.call.id,
        uploadKey: documentUploadInfo.key,
        fileExtension: documentUploadInfo.extension,
        fileName: documentUploadInfo.fileName,
      })
      this.toastService.show('MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_SUCCESS')
      setTimeout(() => {
        const setupContent = document.querySelector('.setup-content')
        if (setupContent !== null) {
          setupContent.scrollTo({ top: setupContent.scrollHeight, behavior: 'smooth' })
        }
      }, 100)
    } catch (error) {
      console.error('Failed to import recording:', error)
    } finally {
      this.isImportingRecording = false
      this.uploadingFile = null
    }
  }

  async completeSetup() {
    try {
      await this.completeSetupMutation.mutateAsync({ callId: this.call.id })
      this.toastService.show('MEETING_SETUP.COMPLETE_SETUP.SUCCESS')
    } catch (error) {
      console.error('Failed to complete setup:', error)
    }
  }

  async removeUploadedRecording() {
    if (this.call.uploadedRecording === null) {
      return
    }
    const recording = this.call.uploadedRecording
    try {
      await this.removeResourceMutation.mutateAsync({
        callId: this.call.id,
        resourceType: recording.resourceType,
        index: recording.index,
        extension: recording.extension,
      })
    } catch (error) {
      console.error('Failed to remove recording:', error)
    }
  }

  async openTemplateSelectionDialog() {
    if (this.shouldConfirmTemplateReplace) {
      const confirmed = await this.showTemplateReplaceConfirmation()
      if (!confirmed) {
        return
      }
    }

    this.templatesService
      .getTemplateModels()
      .pipe(
        filter((result) => result.isSuccess),
        take(1)
      )
      .subscribe((result) => {
        const dialogData: TemplateImportDialogInputData = {
          templates: result.data,
        }
        const dialogRef = this.dialog.open(TemplateImportDialog, {
          width: '500px',
          data: dialogData,
        })

        dialogRef.afterClosed().subscribe((dialogResult: TemplateImportDialogOutputData | null | undefined) => {
          if (dialogResult !== null && dialogResult !== undefined) {
            this.loadTemplateFromModel(dialogResult.templateId)
          }
        })
      })
  }

  async onDetailsSaved(saveData: MeetingDetailsSaveData) {
    await this.updateMetadataMutation.mutateAsync({
      callId: this.call.id,
      data: saveData.metadata,
    })
    const callPatchData = {
      ...(saveData.dateTime !== null && {
        date: saveData.dateTime.date,
        endDate: saveData.dateTime.endDate,
      }),
      ...(saveData.link !== this.call.link && { link: saveData.link }),
    }
    if (Object.keys(callPatchData).length > 0) {
      await this.patchCallMutation.mutateAsync({
        callId: this.call.id,
        data: callPatchData,
      })
    }
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  async updateAgendaType(agendaType: AgendaType) {
    await this.updateAgendaTypeMutation.mutateAsync({ callId: this.call.id, data: { agendaType } })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.call.id, MetricType.CHANGE_AGENDA_TYPE, { agendaType })
  }

  async updateMinutesStyling(styling: Partial<MinutesStyling>) {
    await this.patchCallMutation.mutateAsync({
      callId: this.call.id,
      data: { ...styling, templateApprovedByUser: false },
    })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.call.id, MetricType.CHANGE_MINUTING_STYLES, styling)
  }

  async confirmTemplate() {
    if (this.templateEditor !== undefined && this.templateEditor.hasUnsavedChanges) {
      const cleanedItems = this.templateEditor.getCleanedTemplateItems()
      await this.saveTemplateItems(cleanedItems)
    }

    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: true } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.TEMPLATE_CONFIRMED')
    if (this.isUploadedCall) {
      this.scrollToStep(5)
    }
  }

  private async loadTemplateFromModel(templateId: number) {
    if (this.templateEditor !== undefined) {
      this.templateEditor.clearUnsavedChanges()
    }
    await this.removeUploadedAgenda()
    await this.loadTemplateMutation.mutateAsync({
      callId: this.call.id,
      templateId,
    })
    this.scrollToStep(this.reviewStepNumber)
  }

  async createBlankTemplate() {
    if (this.shouldConfirmTemplateReplace) {
      const confirmed = await this.showTemplateReplaceConfirmation()
      if (!confirmed) {
        return
      }
    }

    await this.removeUploadedAgenda()
    await this.updateTemplateItemsMutation.mutateAsync({
      callId: this.call.id,
      items: [],
    })
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.BLANK_TEMPLATE_CREATED')
    this.scrollToStep(this.reviewStepNumber)
  }

  private async showTemplateReplaceConfirmation(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.TITLE'),
        description: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })
    return await firstValueFrom(dialogRef.afterClosed())
  }

  private async removeUploadedAgenda() {
    const agendaResource = this.call.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA')
    if (agendaResource === undefined) {
      return
    }
    try {
      await this.removeResourceMutation.mutateAsync({
        callId: this.call.id,
        resourceType: agendaResource.resourceType,
        index: agendaResource.index,
        extension: agendaResource.extension,
      })
    } catch (error) {
      console.error('Failed to remove agenda:', error)
    }
  }

  onStepClick(stepIndex: number) {
    if (stepIndex + 1 <= this.currentStep) {
      const elementIndex = this.isUploadedCall && stepIndex > 0 ? stepIndex + 1 : stepIndex
      const element = document.getElementById(`step-${elementIndex}`)
      if (element !== null) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  private scrollToStep(step: number) {
    const elementId = `step-${step - 1}`
    const element = document.getElementById(elementId)
    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const observer = new MutationObserver(() => {
      const target = document.getElementById(elementId)
      if (target !== null) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        observer.disconnect()
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    setTimeout(() => observer.disconnect(), 5000)
  }

  async saveTemplateItems(items: TemplateItem[]) {
    if (this.templateEditor === undefined) {
      return
    }
    await this.updateTemplateItemsMutation.mutateAsync({
      callId: this.call.id,
      items,
    })
    this.templateEditor.confirmSaveSuccess()
  }

  async deleteAttendee(attendee: AdminAgendaAttendee) {
    await this.deleteAttendeeMutation.mutateAsync({ callId: this.call.id, attendeeId: attendee.id })
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_DELETED')
  }

  async updateAttendee(attendee: AdminAgendaAttendee) {
    await this.updateAttendeeMutation.mutateAsync({ callId: this.call.id, data: attendee })
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_UPDATED')
  }

  async addAttendee(attendee: Omit<AdminAgendaAttendee, 'id'>) {
    await this.addAttendeeMutation.mutateAsync({ callId: this.call.id, data: attendee })
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_ADDED')
  }

  isUploadingParticipantDocument = false

  get isParticipantExtractionInProgress(): boolean {
    return this.isUploadingParticipantDocument || this.call.isParticipantExtractionInProgress
  }

  async onParticipantExtractionFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      return
    }
    this.isUploadingParticipantDocument = true
    try {
      await this.extractParticipantsMutation.mutateAsync({
        callId: this.call.id,
        uploadKey: documentUploadInfo.key,
        fileName: documentUploadInfo.fileName,
        fileExtension: documentUploadInfo.extension.toUpperCase(),
      })
      await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: { templateApprovedByUser: false } })
    } catch (error) {
      console.error('Failed to extract participants:', error)
    } finally {
      this.isUploadingParticipantDocument = false
    }
  }

  onTemplateSaved(items: TemplateItem[]) {
    this.saveTemplateItems(items)
  }

  onSaveAsTemplate(items: TemplateItem[]) {
    const dialogRef = this.dialog.open(TemplateSaveAsDialog, {
      width: '400px',
    })

    dialogRef.afterClosed().subscribe(async (result: TemplateSaveAsDialogOutputData | null) => {
      if (result !== null) {
        await this.saveAsTemplateMutation.mutateAsync({
          organizationId: this.organization.id,
          name: result.name,
          items,
        })
      }
    })
  }

  addNewAttendee() {
    this.addAttendee(this.newAttendee as Omit<AdminAgendaAttendee, 'id'>)
    this.newAttendee = { firstName: '', lastName: '', role: null, type: AgendaAttendeeType.MEMBER, honorifics: null }
    this.showAddAttendeeForm = false
  }

  cancelAddAttendee(): void {
    this.newAttendee = { firstName: '', lastName: '', role: null, type: AgendaAttendeeType.MEMBER, honorifics: null }
    this.showAddAttendeeForm = false
  }

  startEditAttendee(attendee: AdminAgendaAttendee) {
    this.editedAttendeeId = attendee.id
    this.editedAttendeeFormData = { ...attendee }
  }

  cancelEditAttendee() {
    this.editedAttendeeId = null
    this.editedAttendeeFormData = {}
  }

  saveAttendee() {
    this.updateAttendee(this.editedAttendeeFormData as AdminAgendaAttendee)
    this.editedAttendeeId = null
    this.editedAttendeeFormData = {}
  }

  async handleFileUpload(request: FileUploadRequest) {
    if (this.templateEditor === undefined) {
      return
    }
    const { blockId, file } = request
    const fileIsValid = this.fileValidationService.validateFile(file, ['pdf'], this.maxCustomDocumentSize)
    if (!fileIsValid) {
      return
    }
    const extension = getFileExtension(file)
    const fileName = getFileName(file)

    this.templateEditor.addPendingSourceToBlock(blockId, fileName)

    try {
      const uploadInfo = await lastValueFrom(this.uploadsService.uploadCustomDocument(file))
      if (uploadInfo.uploadKey === null) {
        throw new Error('Upload key not received for file upload with name ' + fileName)
      }
      const createdResource = await this.importResourceMutation.mutateAsync({
        callId: this.call.id,
        data: {
          uploadKey: uploadInfo.uploadKey,
          fileExtension: extension.toUpperCase(),
          fileType: 'CUSTOM_DOCUMENT',
          fileName,
        },
      })
      this.templateEditor.addSourceToBlock(blockId, createdResource, fileName)
    } catch (error) {
      console.error('Error uploading file', error)
      this.templateEditor.removePendingSourceFromBlock(blockId, fileName)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.showRaw(this.translateService.instant('TEMPLATES.EDITOR.FILE_UPLOAD_FAILED'))
      }
    }
  }
}
